import { Action, PathsV1GroupUuidGetParametersQueryExtend } from "@/api-schema-enums";
import { Can, useAbility } from "@/casl-provider";
import {
  useGroupByUuidQuery,
  type GroupByUuidParameters,
  type GroupByUuidResponse,
} from "@/common/api/group/use-group-by-uuid.query";
import { useGroupDeleteMutation } from "@/common/api/group/use-group-delete.mutation";
import { useGroupRestoreMutation } from "@/common/api/group/use-group-restore.mutation";
import { formatDateToLocale } from "@/common/helpers/format-date";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { useDevice } from "@/common/hooks/use-device";
import { ConfirmationType } from "@/components/confirm-dialog/_types";
import { BaseConfirmDialog } from "@/components/confirm-dialog/base-confirm-dialog";
import { DeleteConfirmDialog } from "@/components/confirm-dialog/predefined/delete-confirm-dialog";
import { DetailHeader } from "@/components/detail-page/detail-header";
import { DetailPageLayout } from "@/components/detail-page/detail-page-layout";
import { DetailTabs } from "@/components/detail-page/detail-tabs";
import type { DetailAction, DetailPermission, DetailTab } from "@/components/detail-page/types";
import { CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import NoMobileView from "@/views/common/no-mobile.view";
import {
  KeyRoundIcon,
  ListChecksIcon,
  PencilIcon,
  RotateCcwIcon,
  ScaleIcon,
  TrashIcon,
  TriangleAlert,
  UsersIcon,
} from "lucide-react";
import { useCallback, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";
import { GroupDetailSkeleton } from "./_components/group-detail-skeleton";
import { GroupDetailTab } from "./_helpers/group-detail-path";
import { AppRolesTab } from "./tabs/app-roles-tab/app-roles-tab";
import { RulesTab } from "./tabs/rules-tab/rules-tab";
import { UsersTab } from "./tabs/users-tab/users-tab";

const GROUP_DETAIL_QUERY_PARAMS = { parameters: { extend: [PathsV1GroupUuidGetParametersQueryExtend.AppRoles] } };

const MANAGE_GROUP_PERMISSION: DetailPermission = { action: Action.Manage, subject: "Group" };

const GROUP_DETAIL_TRACKING_NAME = "ram-group";

export function GroupUuidView(): ReactElement {
  const params = useParams<{ groupUuid: string }>();
  const { isMobile } = useDevice();

  return (
    <Can I="read" a="Group">
      {isMobile ? <NoMobileView homeHref="~/ram/group" /> : <GroupDetail groupUuid={params.groupUuid ?? ""} />}
    </Can>
  );
}

function GroupDetail({ groupUuid }: { groupUuid: string }): ReactElement {
  const { t } = useTranslation(["ram", "common"]);
  const groupQuery = useGroupByUuidQuery(groupUuid, GROUP_DETAIL_QUERY_PARAMS);

  if (groupQuery.isPending) {
    return <GroupDetailSkeleton />;
  }

  return (
    <UuidExistenceWrapper<GroupByUuidParameters, GroupByUuidResponse>
      uuid={groupUuid}
      query={useGroupByUuidQuery}
      queryResult={groupQuery}
      notFoundProps={{
        title: t("ram:GroupNotFound"),
        message: t("ram:GroupDoesNotExist"),
        description: t("ram:PleaseCheckGroupIdOrContactSupport"),
        backTo: "~/ram/group",
        backToLabel: t("ram:BackToGroupOverview"),
      }}
    >
      {groupQuery.data && <GroupDetailContent group={groupQuery.data} />}
    </UuidExistenceWrapper>
  );
}

function GroupDetailContent({ group }: { group: GroupByUuidResponse }): ReactElement {
  const { t } = useTranslation(["ram", "common"]);
  const [, navigate] = useLocation();
  const ability = useAbility();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const { mutateAsync: deleteGroupMutate } = useGroupDeleteMutation();
  const { mutateAsync: restoreGroupMutate } = useGroupRestoreMutation();

  const groupUuid = group.uuid;
  const isDeleted = Boolean(group.deletedAt);
  const canManageGroup = !isDeleted && ability.can(Action.Manage, "Group");
  const canManageRules = !isDeleted && ability.can(Action.Manage, "Rule");

  const handleConfirmDelete = useCallback(async () => {
    await toastMutationStatus({
      mutationFn: () => deleteGroupMutate(isDeleted ? { uuid: groupUuid, force: true } : { uuid: groupUuid }),
      successMessage: t("ram:GroupHasBeenDeleted"),
      errorMessage: t("ram:DeleteGroupError"),
      onSuccess: () => {
        setDeleteDialogOpen(false);
        navigate("~/ram/group");
      },
    });
  }, [deleteGroupMutate, groupUuid, isDeleted, navigate, t]);

  const handleConfirmRestore = useCallback(async () => {
    await toastMutationStatus({
      mutationFn: () => restoreGroupMutate({ uuid: groupUuid }),
      successMessage: t("ram:GroupHasBeenRestored"),
      errorMessage: t("ram:ErrorRestoringGroup"),
      onSuccess: () => {
        setRestoreDialogOpen(false);
      },
    });
  }, [groupUuid, restoreGroupMutate, t]);

  const actions: DetailAction[] = isDeleted
    ? [
        {
          id: "restore",
          label: t("common:Restore"),
          icon: RotateCcwIcon,
          permission: MANAGE_GROUP_PERMISSION,
          onClick: () => setRestoreDialogOpen(true),
        },
        {
          id: "delete-permanently",
          label: t("common:DeletePermanently"),
          icon: TrashIcon,
          permission: MANAGE_GROUP_PERMISSION,
          destructive: true,
          onClick: () => setDeleteDialogOpen(true),
        },
      ]
    : [
        {
          id: "edit",
          label: t("common:Edit"),
          icon: PencilIcon,
          permission: MANAGE_GROUP_PERMISSION,
          onClick: () => navigate(`~/ram/group/${group.uuid}/update`),
        },
        {
          id: "delete",
          label: t("common:Delete"),
          icon: TrashIcon,
          permission: MANAGE_GROUP_PERMISSION,
          destructive: true,
          onClick: () => setDeleteDialogOpen(true),
        },
      ];

  const tabs: DetailTab[] = [
    {
      id: GroupDetailTab.Rules,
      label: t("ram:Rule"),
      icon: ListChecksIcon,
      content: () => <RulesTab groupUuid={group.uuid} canManage={canManageRules} />,
    },
    {
      id: GroupDetailTab.Users,
      label: t("ram:Users"),
      icon: UsersIcon,
      content: () => <UsersTab groupUuid={group.uuid} canManage={canManageGroup} />,
    },
    {
      id: GroupDetailTab.AppRoles,
      label: t("ram:AppRoles"),
      icon: KeyRoundIcon,
      content: () => <AppRolesTab group={group} canManage={canManageGroup} />,
    },
  ];

  return (
    <>
      <DetailPageLayout
        variant={CenteredLayoutVariant.Wide}
        header={
          <DetailHeader
            icon={<ScaleIcon />}
            title={group.name}
            subtitle={t("ram:GroupOverviewDescription")}
            badges={
              isDeleted ? (
                <Badge variant={BadgeVariant.Outlined} colorVariant={BadgeColorVariant.Error}>
                  {t("common:Deleted")}
                </Badge>
              ) : undefined
            }
            actions={actions}
          />
        }
      >
        {group.deletedAt && (
          <Alert variant="error">
            <TriangleAlert className="h-4 w-4" />
            <AlertTitle>{t("ram:Attention")}</AlertTitle>
            <AlertDescription>
              {t("ram:GroupWasDeletedOn", { date: formatDateToLocale(group.deletedAt, { dateOnly: true }) })}
            </AlertDescription>
          </Alert>
        )}
        <DetailTabs sections={tabs} trackingName={GROUP_DETAIL_TRACKING_NAME} />
      </DetailPageLayout>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        label={t("ram:Group")}
        title={isDeleted ? t("ram:PermanentlyDeleteGroup") : t("ram:DeleteGroup")}
        description={isDeleted ? t("ram:PermanentlyDeleteGroupDescription") : t("ram:DeactivateGroupDescription")}
        labelConfirm={isDeleted ? t("common:YesDeletePermanently") : t("ram:YesDelete")}
        name={group.name}
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        requireConfirmation={isDeleted}
        confirmationType={ConfirmationType.Name}
        showIrreversibleWarning={isDeleted}
      />

      <BaseConfirmDialog
        open={restoreDialogOpen}
        title={t("ram:RestoreGroup")}
        description={t("ram:RestoreGroupText")}
        labelCancel={t("common:NoCancel")}
        labelConfirm={t("common:YesRestore")}
        onCancel={() => setRestoreDialogOpen(false)}
        onConfirm={handleConfirmRestore}
      />
    </>
  );
}
